import {
  BOTTLE_SKINS,
  DEFAULT_BOTTLE,
  ECONOMY,
  findBottleSkin,
  type GiftTypeDto,
  isVipActive,
  type PublicProfileDto,
  type StickerPackDto,
} from '@spin/shared';
import { useEffect, useState } from 'react';
import { game } from '../api/game';
import { clientId, http } from '../api/http';
import { Panel } from '../components/Dialog';
import { Spinner } from '../components/common';
import { Emoji } from '../components/Emoji';
import { CloseIcon, PriceHeart } from '../components/icons';
import { reportError, useT } from '../i18n';
import { useRoom } from '../store/room';
import { useSession } from '../store/session';
import { useUi } from '../store/ui';
import { tg } from '../tg/bridge';
import { BottleArt } from './Bottle';

let giftsCache: Promise<GiftTypeDto[]> | null = null;
export function loadGifts(): Promise<GiftTypeDto[]> {
  giftsCache ??= http.get<GiftTypeDto[]>('/gifts').catch((err: unknown) => {
    giftsCache = null;
    throw err;
  });
  return giftsCache;
}

/** Panels over the chat, below the table. */
export function TableSheets() {
  const sheet = useUi((s) => s.sheet);
  if (!sheet) return null;
  const userId = String(sheet.props?.userId ?? '');
  switch (sheet.kind) {
    case 'player':
      return <GiftPanel userId={userId} />;
    case 'dj':
      return (
        <DjPanel
          toUserId={sheet.props?.toUserId ? userId : undefined}
          name={String(sheet.props?.name ?? '')}
          video={Boolean(sheet.props?.video)}
        />
      );
    case 'stickers':
      return <StickersPopup />;
    case 'bottle':
      return <BottlePanel />;
    default:
      return null;
  }
}

/** A price with the small red heart, as under every item in the reference. */
export function HeartPrice({ value }: { value: number }) {
  return (
    <span className="price">
      <PriceHeart />
      {value}
    </span>
  );
}

/**
 * Tapping a player: "Send a gift to <name>" covers the top bar (profile button on the left, add friend on the
 * right) and the gift catalogue replaces the chat. A tap on a gift sends it.
 */
function GiftPanel({ userId }: { userId: string }) {
  const t = useT();
  const me = useSession((s) => s.me)!;
  const seat = useRoom((s) => s.room?.seats.find((x) => x?.userId === userId) ?? null);
  const { openSheet, openDialog, closeSheet } = useUi.getState();
  const [gifts, setGifts] = useState<GiftTypeDto[] | null>(null);
  const [relation, setRelation] = useState<PublicProfileDto['relation'] | null>(null);
  const [profileName, setProfileName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const vip = isVipActive(me.vipUntil);
  const isMe = userId === me.id;

  useEffect(() => {
    loadGifts().then(setGifts).catch(reportError);
    if (!isMe)
      http
        .get<PublicProfileDto>(`/users/${userId}`)
        .then((p) => {
          setRelation(p.relation);
          setProfileName(p.displayName);
        })
        .catch(() => undefined);
  }, [userId, isMe]);

  const send = async (gift: GiftTypeDto) => {
    if (gift.vipOnly && !vip) {
      openDialog('vip');
      return;
    }
    if (gift.price > me.balance) {
      openDialog('shop');
      return;
    }
    if (gift.effect === 'song') {
      // Songs are dedicated through the DJ: pick the video first.
      openSheet('dj', { userId, toUserId: true, name: seat?.name ?? '', video: gift.code === 'music_video' });
      return;
    }
    setBusy(true);
    try {
      await game.emit('gift.send', { toUserId: userId, giftTypeId: gift.id, clientTxId: clientId() });
      tg.haptic('success');
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  const addFriend = async () => {
    setBusy(true);
    try {
      const res = await http.post<{ relation: PublicProfileDto['relation'] }>(`/friends/${userId}`);
      setRelation(res.relation);
      tg.haptic('light');
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="gift-head" data-testid="gift-head">
        <button
          className="gift-head-profile"
          onClick={() => openDialog('profile', { userId })}
          aria-label={t('player.profile')}
          data-testid="open-profile"
        >
          <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden>
            <circle cx="12" cy="12" r="12" fill="#4a90d9" />
            <circle cx="12" cy="9.5" r="4" fill="#fff" />
            <path
              d="M4.8 19.4c1.4-3 4-4.4 7.2-4.4s5.8 1.4 7.2 4.4A11 11 0 0 1 12 23a11 11 0 0 1-7.2-3.6Z"
              fill="#fff"
            />
          </svg>
        </button>
        <div className="gift-head-title">
          {t('gifts.send_to')}
          <b className="ellipsis">{seat?.name ?? profileName ?? ''}</b>
        </div>
        {!isMe && relation !== null && relation !== 'friends' && (
          <button
            className="round-plus"
            disabled={busy || relation === 'outgoing'}
            onClick={() => void addFriend()}
            aria-label={relation === 'incoming' ? t('friends.accept') : t('friends.add')}
            title={relation === 'incoming' ? t('friends.accept') : t('friends.add')}
            data-testid="friend-button"
            data-relation={relation}
          >
            {relation === 'outgoing' ? '…' : '+'}
          </button>
        )}
        <button className="gift-head-close" onClick={closeSheet} aria-label={t('common.close')}>
          <CloseIcon size={16} />
        </button>
      </div>
      <div className="panel" role="dialog" data-testid="gift-panel">
        <div className="panel-body">
          {!gifts ? (
            <Spinner />
          ) : (
            <div className="grid-items">
              {gifts.map((g) => (
                <button
                  key={g.id}
                  className={g.vipOnly && !vip ? 'item-cell locked' : 'item-cell'}
                  disabled={busy || isMe}
                  onClick={() => void send(g)}
                  title={g.name}
                  data-testid={`gift-${g.code}`}
                >
                  <span className="art">
                    <Emoji e={g.emoji} />
                  </span>
                  {g.vipOnly && !vip ? <span className="price">VIP</span> : <HeartPrice value={g.price} />}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          className="panel-close"
          onClick={closeSheet}
          aria-label={t('common.close')}
          data-testid="panel-close"
        >
          <CloseIcon size={20} />
        </button>
      </div>
    </>
  );
}

/** Ordering a song or a music video for the table (or dedicating one to a player). */
function DjPanel({ toUserId, name, video: asVideo }: { toUserId?: string; name: string; video: boolean }) {
  const t = useT();
  const price = asVideo ? ECONOMY.djVideoPrice : ECONOMY.djPrice;
  const queue = useRoom((s) => s.room?.music.queue.length ?? 0);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const order = async () => {
    setBusy(true);
    try {
      await game.emit('dj.order', {
        url: url.trim(),
        clientTxId: clientId(),
        ...(toUserId ? { toUserId } : {}),
        ...(asVideo ? { video: true } : {}),
      });
      tg.haptic('success');
      useUi.getState().closeSheet();
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel
      head={
        <div className="panel-title">
          {toUserId
            ? t('table.dj_dedicate', { name })
            : asVideo
              ? t('table.order_video', { price })
              : t('table.order_song', { price })}
        </div>
      }
      testId="dj-panel"
    >
      <div className="dj-form">
        <p className="muted">{t('table.dj_hint')}</p>
        <input
          className="input"
          placeholder="https://youtu.be/…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          inputMode="url"
          data-testid="dj-url"
        />
        <button
          className="btn btn-green btn-block"
          disabled={busy || url.trim().length < 5}
          onClick={() => void order()}
          data-testid="dj-order"
        >
          {t('table.dj_order', { price })}
        </button>
        {queue > 0 && <p className="muted">{t('table.dj_queue', { count: queue })}</p>}
      </div>
    </Panel>
  );
}

/**
 * "Change the bottle": a new bottle for the whole table. A tap picks a bottle and a second tap on the button pays,
 * so nothing is bought by accident.
 */
function BottlePanel() {
  const t = useT();
  const me = useSession((s) => s.me)!;
  const current = useRoom((s) => s.room?.bottleSkin ?? DEFAULT_BOTTLE);
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const vip = isVipActive(me.vipUntil);
  const skin = picked ? findBottleSkin(picked) : null;

  const put = async () => {
    if (!skin) return;
    if (skin.vipOnly && !vip) {
      useUi.getState().openDialog('vip');
      return;
    }
    setBusy(true);
    try {
      await game.emit('bottle.change', { skin: skin.code, clientTxId: clientId() });
      tg.haptic('success');
      useUi.getState().closeSheet();
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel head={<div className="panel-title">{t('bottle.title')}</div>} testId="bottle-panel">
      <div className="grid-items bottles">
        {BOTTLE_SKINS.filter((b) => b.code !== DEFAULT_BOTTLE).map((b) => {
          const onTable = current === b.code;
          const locked = Boolean(b.vipOnly) && !vip;
          return (
            <button
              key={b.code}
              className={['item-cell', picked === b.code && 'picked', locked && 'locked']
                .filter(Boolean)
                .join(' ')}
              disabled={onTable}
              onClick={() => setPicked(b.code)}
              title={t(`bottle.${b.code}`)}
              data-testid={`bottle-${b.code}`}
            >
              <span className="art bottle-art">
                <BottleArt skin={b.code} />
              </span>
              {onTable ? (
                <span className="price">✓</span>
              ) : locked ? (
                <span className="price">VIP</span>
              ) : (
                <HeartPrice value={b.price} />
              )}
            </button>
          );
        })}
      </div>
      {skin && (
        <button
          className="btn btn-green bottle-put"
          disabled={busy}
          onClick={() => void put()}
          data-testid="put-bottle"
        >
          {t('bottle.put', { price: skin.price })}
        </button>
      )}
    </Panel>
  );
}

/** "My stickers": a small card over the chat; a tap on a sticker shows it to the table (a paid gesture). */
function StickersPopup() {
  const t = useT();
  const close = useUi.getState().closeSheet;
  const [packs, setPacks] = useState<StickerPackDto[] | null>(null);
  const [tab, setTab] = useState('basic');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    http.get<StickerPackDto[]>('/stickers').then(setPacks).catch(reportError);
  }, []);
  useEffect(() => tg.backButton.show(close), [close]);
  const pack = packs?.find((p) => p.code === tab);

  const send = async (index: number) => {
    setBusy(true);
    try {
      await game.emit('sticker.send', { pack: tab, index, clientTxId: clientId() });
      tg.haptic('light');
      close();
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };
  const unlock = async () => {
    setBusy(true);
    try {
      const res = await http.post<{ packs: StickerPackDto[]; balance: number }>('/stickers/buy', {
        pack: tab,
      });
      setPacks(res.packs);
      useSession.getState().setBalance(res.balance);
      tg.haptic('success');
    } catch (err) {
      reportError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stickers-layer" onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="stickers-card" role="dialog" data-testid="stickers">
        <button className="dialog-close" onClick={close} aria-label={t('common.close')}>
          <CloseIcon />
        </button>
        <div className="stickers-title">
          {t('stickers.title')} <span aria-hidden>📂</span>
        </div>
        {packs && packs.length > 1 && (
          <div className="sticker-tabs">
            {packs.map((p) => (
              <button key={p.code} className={tab === p.code ? 'active' : ''} onClick={() => setTab(p.code)}>
                <Emoji e={p.stickers[0]} />
              </button>
            ))}
          </div>
        )}
        {!pack ? (
          <Spinner />
        ) : (
          <>
            <div className={pack.owned ? 'sticker-grid' : 'sticker-grid locked'}>
              {pack.stickers.map((s, i) => (
                <button
                  key={i}
                  disabled={busy || !pack.owned}
                  onClick={() => void send(i)}
                  data-testid={`sticker-${tab}-${i}`}
                >
                  <Emoji e={s} />
                </button>
              ))}
            </div>
            <p className="sticker-note">
              {pack.owned
                ? t('stickers.cost', { price: ECONOMY.gesturePrice })
                : pack.price === null
                  ? t('stickers.vip_only')
                  : ''}
            </p>
            {!pack.owned && pack.price !== null && (
              <button className="btn btn-green btn-block" disabled={busy} onClick={() => void unlock()}>
                {t('stickers.buy', { price: pack.price })}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
